import { createLogger } from '@/utils/logger';

type AuthTransitionState = 'idle' | 'draining' | 'logged_out';

interface InflightRequestMeta {
  controller: AbortController;
  url: string;
}

const AUTH_ALLOWLIST = [
  '/v1/auth/logout',
  '/v1/auth/login',
  '/v1/auth/refresh',
];
const logger = createLogger('AuthSessionManager');

let authTransitionState: AuthTransitionState = 'idle';
let requestSeq = 0;
const inflightRequests = new Map<number, InflightRequestMeta>();

function normalizeUrl(url?: string): string {
  if (!url) return '';
  return String(url);
}

export function isAuthAllowlistRequest(url?: string): boolean {
  const requestUrl = normalizeUrl(url);
  return AUTH_ALLOWLIST.some((path) => requestUrl.includes(path));
}

export function isLogoutDraining(): boolean {
  return authTransitionState === 'draining';
}

export function beginLogoutDrain(): void {
  if (authTransitionState === 'draining') return;
  authTransitionState = 'draining';
  logger.info('进入登出排水状态');
  abortInflightNonAuthRequests('logout-draining');
}

export function finishLogout(): void {
  authTransitionState = 'logged_out';
  logger.info('登出完成，状态切换为logged_out');
}

export function resetAuthTransitionState(): void {
  if (authTransitionState !== 'idle') {
    logger.info('重置认证过渡状态到idle', { previous: authTransitionState });
  }
  authTransitionState = 'idle';
}

export function registerInflightRequest(url?: string): {
  requestId: number;
  signal: AbortSignal;
} | null {
  if (typeof AbortController === 'undefined') {
    return null;
  }
  const controller = new AbortController();
  const requestId = ++requestSeq;
  inflightRequests.set(requestId, {
    controller,
    url: normalizeUrl(url),
  });
  return {
    requestId,
    signal: controller.signal,
  };
}

export function completeInflightRequest(requestId?: number): void {
  if (!requestId) return;
  inflightRequests.delete(requestId);
}

export function abortInflightNonAuthRequests(reason = 'logout-draining'): void {
  let abortedCount = 0;
  inflightRequests.forEach((meta, requestId) => {
    if (!isAuthAllowlistRequest(meta.url)) {
      meta.controller.abort(reason);
      inflightRequests.delete(requestId);
      abortedCount += 1;
    }
  });
  logger.info('取消在途非鉴权请求', { reason, abortedCount });
}

export class LogoutDrainingError extends Error {
  url?: string;

  constructor(url?: string) {
    super('LOGOUT_DRAINING');
    this.name = 'LogoutDrainingError';
    this.url = url;
  }
}
